import type { RouteRecordRaw } from 'vue-router'

/**
 * @file 新版导航体系（/next）路由树
 *
 * 旧版 /app 界面已下线，本文件是业务界面的唯一路由来源：
 * - 外壳为 NextLayout，页面全部位于 views/next/ 下；
 * - 页面权限由 views/next/nav/model.ts 的「模块 × 页面」矩阵在应用层控制；
 * - 旧版 /app 链接在 router/index.ts 统一重定向到新版工作台。
 *
 * meta.bleed：方案 C 露桌布页（橱窗 / 双板列表等）；内容区白板让位，模块卡浮在桌布上。
 * 全部页面统一纱页头浮桌布（见 views/next/styles/page-frame.scss）；
 * 置 true 时内容区白板也让位，模块卡直接坐桌布；
 * 作业型页面（表单 / 表格 / 详情）不置此标记，白板保留为整屏唯一大模块。
 */
const NEXT_ROUTE_TITLES = {
  workbench: '工作台 · 新版导航',
  aiAssistant: 'AI 助理 · 新版导航',
  help: '帮助中心 · 新版导航',
  // 成长评价
  indicators: '指标体系 · 新版导航',
  planCenter: '活动管理 · 新版导航',
  classGrowth: '班级成长 · 新版导航',
  evaluation: '评价中心 · 新版导航',
  instant: '即时点评 · 新版导航',
  growthLog: '成长记录 · 新版导航',
  incentive: '成长激励 · 新版导航',
  mall: '商城管理 · 新版导航',
  reports: '报告中心 · 新版导航',
  insight: '成长洞察中心 · 新版导航',
  resources: '资源中心 · 新版导航',
  // 社团管理
  clubOverview: '社团总览 · 新版导航',
  clubSeasons: '招新季管理 · 新版导航',
  clubReview: '申请审核 · 新版导航',
  // 课表管理
  scheduleTasks: '排课任务 · 新版导航',
  scheduleTaskCreate: '新建排课任务 · 新版导航',
  scheduleTaskDetail: '排课任务详情 · 新版导航',
  scheduleQuery: '课表查询 · 新版导航',
  scheduleMaintenance: '课表维护 · 新版导航',
  myTimetable: '我的课表 · 新版导航',
  // 教务管理
  selection: '选科任务 · 新版导航',
  selectionTaskCreate: '新建选科任务 · 新版导航',
  schoolBased: '校本选课 · 新版导航',
  schoolBasedCreate: '新建校本选课任务 · 新版导航',
  enrollment: '入学分班 · 新版导航',
  enrollmentCreate: '新建入学分班任务 · 新版导航',
  subjectClass: '选科分班 · 新版导航',
  subjectClassCreate: '新建选科分班任务 · 新版导航',
  exams: '考试管理 · 新版导航',
  examsCreate: '新建考试活动 · 新版导航',
  scores: '成绩发布 · 新版导航',
  scoresEntry: '成绩录入 · 新版导航',
  evaluationTasks: '评价任务 · 新版导航',
  evaluationTaskCreate: '新建评价任务 · 新版导航',
  // 数据管理
  students: '学生管理 · 新版导航',
  staff: '教职工管理 · 新版导航',
  positions: '岗位管理 · 新版导航',
  subjects: '科目管理 · 新版导航',
  academicYears: '学年学期 · 新版导航',
  // 系统设置
  organization: '组织架构 · 新版导航',
  gradeClass: '年级班级 · 新版导航',
  buildingRoom: '楼栋教室 · 新版导航',
  scheduleSettings: '作息时间管理 · 新版导航',
  roles: '角色权限 · 新版导航',
  passwords: '密码管理 · 新版导航',
  assistant: '助理设置 · 新版导航'
}

export const nextRoutes: RouteRecordRaw[] = [
  {
    path: '/next',
    name: 'NextLayout',
    component: () => import('@/layouts/NextLayout.vue'),
    redirect: '/next/workbench',
    meta: { requiresAuth: true, title: '新版导航演示' },
    children: [
      {
        path: 'workbench',
        name: 'NextWorkbench',
        component: () => import('@/views/next/workbench/WorkbenchPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.workbench, bleed: true }
      },
      // ── 跨模块功能入口（顶栏图标直达） ──
      {
        path: 'ai-assistant',
        name: 'NextAiAssistant',
        component: () => import('@/views/next/function/AiAssistantPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.aiAssistant }
      },
      {
        path: 'help',
        name: 'NextHelp',
        component: () => import('@/views/next/function/HelpCenterPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.help }
      },
      // ── 成长评价 ──
      {
        path: 'wuyu/indicators',
        name: 'NextWuyuIndicators',
        component: () => import('@/views/next/wuyu/IndicatorsPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.indicators, bleed: true }
      },
      {
        // 工具箱：采集模板 / 激励模板 / 评价表单三类可复用资产
        path: 'wuyu/toolbox',
        name: 'NextWuyuToolbox',
        component: () => import('@/views/next/wuyu/ToolboxPage.vue'),
        meta: { title: '工具箱 · 新版导航', permission: 'wuyu:plan-center' }
      },
      {
        // 活动管理列表：保留原概览驾驶舱 + 混合列表；新建/编辑走方案蓝图（3 类载体 × 6 种评价方式）
        path: 'wuyu/plan-center',
        name: 'NextWuyuPlanCenter',
        component: () => import('@/views/next/wuyu/PlanCenterPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.planCenter }
      },
      {
        // 新建活动选择页：先选类型，再从空白开始或引用资源（独立地址，刷新/后退不丢）
        path: 'wuyu/plan-center/choose',
        name: 'NextWuyuPlanCenterChoose',
        component: () => import('@/views/next/wuyu/PlanCenterPage.vue'),
        meta: { title: '新建活动 · 新版导航', permission: 'wuyu:plan-center' }
      },
      {
        // 方案蓝图 · 新建（5 块配置，右侧面板逐块完成）
        path: 'wuyu/plan-center/new',
        name: 'NextWuyuSchemeCreate',
        component: () => import('@/views/next/wuyu/SchemeBlueprintPage.vue'),
        meta: { title: '新建活动 · 新版导航' }
      },
      {
        // 方案蓝图 · 编辑（进行中方案进入时自动暂停，离开自动恢复）
        path: 'wuyu/plan-center/:id/edit',
        name: 'NextWuyuSchemeEdit',
        component: () => import('@/views/next/wuyu/SchemeBlueprintPage.vue'),
        meta: { title: '编辑活动 · 新版导航' }
      },
      {
        path: 'wuyu/plan-center/data/scheme/:id',
        name: 'NextWuyuSchemeData',
        component: () => import('@/views/next/wuyu/scheme-data/SchemeDataPage.vue'),
        meta: { title: '活动数据 · 新版导航', permission: 'wuyu:plan-center' }
      },
      {
        // 即时点评：课堂场景最高频；方案 C 双板露桌布（bleed）
        path: 'wuyu/instant',
        name: 'NextWuyuInstant',
        component: () => import('@/views/next/wuyu/InstantGrantPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.instant, bleed: true }
      },
      {
        // 成长活动：活动记录 + 活动审核 + 成长动态（纵向三分区，无页签）
        path: 'wuyu/activities',
        name: 'NextWuyuActivities',
        component: () => import('@/views/next/wuyu/ActivityCenterPage.vue'),
        meta: { title: '成长活动 · 新版导航', bleed: true }
      },
      {
        // 班级成长：班主任为本班创建和管理活动
        path: 'wuyu/class-growth',
        name: 'NextWuyuClassGrowth',
        component: () => import('@/views/next/wuyu/ClassGrowthPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.classGrowth, bleed: true }
      },
      {
        // 班级成长 · 新建活动：复用方案蓝图，参与对象锁本班，只认 wuyu:class-growth
        path: 'wuyu/class-growth/new',
        name: 'NextWuyuClassGrowthCreate',
        component: () => import('@/views/next/wuyu/SchemeBlueprintPage.vue'),
        meta: { title: '新建班级活动 · 新版导航' }
      },
      {
        // 班级成长 · 编辑活动：仅创建人可进入
        path: 'wuyu/class-growth/:id/edit',
        name: 'NextWuyuClassGrowthEdit',
        component: () => import('@/views/next/wuyu/SchemeBlueprintPage.vue'),
        meta: { title: '编辑班级活动 · 新版导航' }
      },
      {
        path: 'wuyu/incentive',
        name: 'NextWuyuIncentive',
        component: () => import('@/views/next/wuyu/IncentiveCenterPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.incentive, bleed: true }
      },
      {
        // 商城管理：从激励中心拆出的独立二级页面（商品管理 + 兑换记录 + 学生余额 + 虚拟商城）
        path: 'wuyu/mall',
        name: 'NextWuyuMall',
        component: () => import('@/views/next/wuyu/MallManagePage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.mall, bleed: true }
      },
      {
        path: 'wuyu/incentive/plan/:id',
        name: 'NextWuyuIncentivePlanDetail',
        component: () => import('@/views/next/wuyu/IncentivePlanDetailPage.vue'),
        meta: { title: '勋章方案详情 · 新版导航' }
      },
      {
        path: 'wuyu/reports',
        name: 'NextWuyuReports',
        component: () => import('@/views/next/wuyu/ReportsPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.reports, bleed: true }
      },
      {
        path: 'wuyu/reports/all',
        name: 'NextWuyuReportSemesters',
        component: () => import('@/views/next/wuyu/reports/ReportSemestersPage.vue'),
        meta: { title: '全部学期 · 报告中心', bleed: true }
      },
      {
        path: 'wuyu/reports/view/:type/:semesterId/:scopeId',
        name: 'NextWuyuReportReader',
        component: () => import('@/views/next/wuyu/reports/ReportReaderPage.vue'),
        meta: { title: '报告阅读 · 报告中心', bleed: true, reportShell: true }
      },
      {
        path: 'wuyu/reports/jobs/:jobId',
        name: 'NextWuyuReportJob',
        component: () => import('@/views/next/wuyu/reports/ReportJobPage.vue'),
        meta: { title: '生成任务 · 报告中心', bleed: true }
      },
      {
        path: 'wuyu/reports/exports',
        name: 'NextWuyuReportExports',
        component: () => import('@/views/next/wuyu/reports/ReportExportsPage.vue'),
        meta: { title: '导出记录 · 报告中心', bleed: true }
      },
      {
        path: 'wuyu/reports/exports/:batchId',
        name: 'NextWuyuReportExportBatch',
        component: () => import('@/views/next/wuyu/reports/ReportExportBatchPage.vue'),
        meta: { title: '导出批次 · 报告中心', bleed: true }
      },
      {
        path: 'wuyu/reports/parent-views',
        name: 'NextWuyuReportParentViews',
        component: () => import('@/views/next/wuyu/reports/ReportParentViewsPage.vue'),
        meta: { title: '家长查看 · 报告中心', bleed: true }
      },
      {
        path: 'wuyu/reports/templates',
        name: 'NextWuyuReportTemplates',
        component: () => import('@/views/next/wuyu/reports/ReportTemplatesPage.vue'),
        meta: { title: '品牌模板 · 报告中心', bleed: true }
      },
      {
        path: 'wuyu/insight',
        name: 'NextWuyuInsight',
        component: () => import('@/views/next/wuyu/InsightPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.insight, bleed: true }
      },
      {
        // 2026-10-21 生切：案例中心并入资源中心
        path: 'wuyu/cases',
        redirect: { path: '/next/wuyu/resources', query: { tab: 'cases' } }
      },
      {
        path: 'wuyu/resources',
        name: 'NextWuyuResources',
        component: () => import('@/views/next/wuyu/WuyuResourcesPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.resources, bleed: true }
      },
      // ── 社团管理（作业型页面：纱页头 + 白底板，不置 bleed） ──
      {
        path: 'club/overview',
        name: 'NextClubOverview',
        component: () => import('@/views/next/club/ClubOverviewPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.clubOverview }
      },
      {
        path: 'club/seasons',
        name: 'NextClubSeasons',
        component: () => import('@/views/next/club/ClubSeasonsPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.clubSeasons }
      },
      {
        path: 'club/review',
        name: 'NextClubReview',
        component: () => import('@/views/next/club/ClubReviewPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.clubReview }
      },
      // ── 课表管理 ──
      {
        path: 'schedule/tasks/new',
        name: 'NextScheduleTaskCreate',
        component: () => import('@/views/next/schedule/ScheduleTaskCreatePage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.scheduleTaskCreate }
      },
      {
        path: 'schedule/tasks/:id',
        name: 'NextScheduleTaskDetail',
        component: () => import('@/views/next/schedule/ScheduleTaskDetailPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.scheduleTaskDetail }
      },
      {
        path: 'schedule/tasks',
        name: 'NextScheduleTasks',
        component: () => import('@/views/next/schedule/ScheduleTasksPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.scheduleTasks }
      },
      {
        path: 'schedule/query',
        name: 'NextScheduleQuery',
        component: () => import('@/views/next/schedule/ScheduleQueryPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.scheduleQuery }
      },
      {
        path: 'schedule/maintenance',
        name: 'NextScheduleMaintenance',
        component: () => import('@/views/next/schedule/ScheduleMaintenancePage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.scheduleMaintenance }
      },
      {
        path: 'schedule/my',
        name: 'NextScheduleMy',
        component: () => import('@/views/next/schedule/MyTimetablePage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.myTimetable }
      },
      // ── 教务管理 ──
      {
        path: 'edu/selection',
        name: 'NextEduSelection',
        component: () => import('@/views/next/edu/selection/SelectionPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.selection }
      },
      {
        path: 'edu/selection/new',
        name: 'NextEduSelectionCreate',
        component: () => import('@/views/next/edu/selection/SelectionWizardPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.selectionTaskCreate }
      },
      {
        path: 'edu/school-based',
        name: 'NextEduSchoolBased',
        component: () => import('@/views/next/edu/EduSchoolBasedPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.schoolBased }
      },
      {
        // 校本选课任务新建/编辑向导（复杂表单走独立页，不用抽屉承载）
        path: 'edu/school-based/new',
        name: 'NextEduSchoolBasedCreate',
        component: () => import('@/views/next/edu/school-based/SchoolBasedTaskCreatePage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.schoolBasedCreate }
      },
      {
        path: 'edu/enrollment',
        name: 'NextEduEnrollment',
        component: () => import('@/views/next/edu/EduEnrollmentPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.enrollment }
      },
      {
        // 入学分班任务新建/编辑向导（复杂表单走独立页，不用抽屉承载）
        path: 'edu/enrollment/new',
        name: 'NextEduEnrollmentCreate',
        component: () => import('@/views/next/edu/enrollment/EnrollmentTaskCreatePage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.enrollmentCreate }
      },
      {
        path: 'edu/subject-class',
        name: 'NextEduSubjectClass',
        component: () => import('@/views/next/edu/subject-class/SubjectClassPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.subjectClass }
      },
      {
        path: 'edu/subject-class/new',
        name: 'NextEduSubjectClassCreate',
        component: () => import('@/views/next/edu/subject-class/SubjectAssignmentWizardPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.subjectClassCreate }
      },
      {
        path: 'edu/exams',
        name: 'NextEduExams',
        component: () => import('@/views/next/edu/EduExamsPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.exams }
      },
      {
        // 考试活动新建/编辑（单步表单页，?taskId= 编辑草稿）
        path: 'edu/exams/new',
        name: 'NextEduExamsCreate',
        component: () => import('@/views/next/edu/exams/ExamActivityCreatePage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.examsCreate }
      },
      {
        path: 'edu/scores',
        name: 'NextEduScores',
        component: () => import('@/views/next/edu/EduScoresPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.scores }
      },
      {
        // 成绩录入/导入（单步表单页：?examId= 录入、?taskId= 导入）
        path: 'edu/scores/new',
        name: 'NextEduScoresEntry',
        component: () => import('@/views/next/edu/scores/ScoreTaskPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.scoresEntry }
      },
      {
        path: 'edu/evaluation-tasks',
        name: 'NextEduEvaluationTasks',
        component: () => import('@/views/next/edu/EduEvaluationPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.evaluationTasks }
      },
      {
        // 评价任务创建（单步表单页，生成后不可编辑）
        path: 'edu/evaluation-tasks/new',
        name: 'NextEduEvaluationCreate',
        component: () => import('@/views/next/edu/evaluation/EvaluationTaskPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.evaluationTaskCreate }
      },
      // ── 数据管理 ──
      {
        path: 'data/students',
        name: 'NextDataStudents',
        component: () => import('@/views/next/data/DataStudentsPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.students }
      },
      {
        // 学生新建/编辑：复杂表单走独立页，不与列表同屏
        path: 'data/students/new',
        name: 'NextDataStudentsCreate',
        component: () => import('@/views/next/data/DataStudentsFormPage.vue'),
        meta: { title: '新增学生 · 新版导航' }
      },
      {
        path: 'data/students/:id/edit',
        name: 'NextDataStudentsEdit',
        component: () => import('@/views/next/data/DataStudentsFormPage.vue'),
        meta: { title: '编辑学生 · 新版导航' }
      },
      {
        path: 'data/staff',
        name: 'NextDataStaff',
        component: () => import('@/views/next/data/DataStaffPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.staff }
      },
      {
        path: 'data/positions',
        name: 'NextDataPositions',
        component: () => import('@/views/next/data/DataPositionsPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.positions }
      },
      {
        // 教职工新建/编辑：复杂表单走独立页，不与列表同屏
        path: 'data/staff/new',
        name: 'NextDataStaffCreate',
        component: () => import('@/views/next/data/DataStaffFormPage.vue'),
        meta: { title: '新增教职工 · 新版导航' }
      },
      {
        path: 'data/staff/:id/edit',
        name: 'NextDataStaffEdit',
        component: () => import('@/views/next/data/DataStaffFormPage.vue'),
        meta: { title: '编辑教职工 · 新版导航' }
      },
      {
        path: 'data/subjects',
        name: 'NextDataSubjects',
        component: () => import('@/views/next/data/DataSubjectsPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.subjects }
      },
      {
        path: 'data/academic-years',
        name: 'NextDataAcademicYears',
        component: () => import('@/views/next/data/DataAcademicYearsPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.academicYears }
      },
      // ── 系统设置 ──
      {
        path: 'settings/organization',
        name: 'NextSettingsOrganization',
        component: () => import('@/views/next/settings/SettingsOrganizationPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.organization }
      },
      {
        path: 'settings/grade-class',
        name: 'NextSettingsGradeClass',
        component: () => import('@/views/next/settings/SettingsGradeClassPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.gradeClass }
      },
      {
        // 班级/年级/学段新建与编辑：复杂表单走独立页，不与列表同屏
        path: 'settings/grade-class/class/new',
        name: 'NextSettingsClassCreate',
        component: () => import('@/views/next/settings/GradeClassClassFormPage.vue'),
        meta: { title: '新增班级 · 新版导航' }
      },
      {
        path: 'settings/grade-class/class/:id/edit',
        name: 'NextSettingsClassEdit',
        component: () => import('@/views/next/settings/GradeClassClassFormPage.vue'),
        meta: { title: '编辑班级 · 新版导航' }
      },
      {
        path: 'settings/grade-class/grade/new',
        name: 'NextSettingsGradeCreate',
        component: () => import('@/views/next/settings/GradeClassGradeFormPage.vue'),
        meta: { title: '新增年级 · 新版导航' }
      },
      {
        path: 'settings/grade-class/grade/:id/edit',
        name: 'NextSettingsGradeEdit',
        component: () => import('@/views/next/settings/GradeClassGradeFormPage.vue'),
        meta: { title: '编辑年级 · 新版导航' }
      },
      {
        path: 'settings/grade-class/stage/new',
        name: 'NextSettingsStageCreate',
        component: () => import('@/views/next/settings/GradeClassStageFormPage.vue'),
        meta: { title: '新增学段 · 新版导航' }
      },
      {
        path: 'settings/grade-class/stage/:id/edit',
        name: 'NextSettingsStageEdit',
        component: () => import('@/views/next/settings/GradeClassStageFormPage.vue'),
        meta: { title: '编辑学段 · 新版导航' }
      },
      {
        path: 'settings/building-room',
        name: 'NextSettingsBuildingRoom',
        component: () => import('@/views/next/settings/SettingsBuildingRoomPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.buildingRoom }
      },
      {
        path: 'settings/schedule',
        name: 'NextSettingsSchedule',
        component: () => import('@/views/next/settings/SettingsSchedulePage.vue'),
        // 与指标体系同款：页头外置，学期状态条贴桌布，作息与配置双板浮卡
        meta: { title: NEXT_ROUTE_TITLES.scheduleSettings, bleed: true }
      },
      {
        path: 'settings/roles',
        name: 'NextSettingsRoles',
        component: () => import('@/views/next/settings/SettingsRolesPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.roles }
      },
      {
        // 角色新建/编辑：表单走独立页，不与列表同屏
        path: 'settings/roles/new',
        name: 'NextSettingsRolesCreate',
        component: () => import('@/views/next/settings/RoleFormPage.vue'),
        meta: { title: '新建角色 · 新版导航' }
      },
      {
        path: 'settings/roles/:id/edit',
        name: 'NextSettingsRolesEdit',
        component: () => import('@/views/next/settings/RoleFormPage.vue'),
        meta: { title: '编辑角色 · 新版导航' }
      },
      {
        path: 'settings/passwords',
        name: 'NextSettingsPasswords',
        component: () => import('@/views/next/settings/SettingsPasswordsPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.passwords }
      },
      {
        path: 'settings/assistant',
        name: 'NextSettingsAssistant',
        component: () => import('@/views/next/settings/SettingsAssistantPage.vue'),
        meta: { title: NEXT_ROUTE_TITLES.assistant }
      }
    ]
  }
]
